import { getGridSingleSelectOperators, getGridStringOperators, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-pro'
import React from 'react'

import T from 'T'
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_STATUS,
  isUnread,
  type Notification,
  type NotificationCategory,
  type NotificationStatus,
} from 'types/modules/notification-center/notification'

import { CATEGORY_LABELS, NOTIFICATION_FILTER_FIELD, READ_STATE, STATUS_LABELS, areaFor } from './notificationCenterViews'
import { notificationDateOperators } from './notificationDateOperators'
import NotificationRowCard from './NotificationRowCard'

const renderRowCard = ({ row }: GridRenderCellParams<Notification>) => <NotificationRowCard row={row} />

const STATUS_OPTIONS = (Object.values(NOTIFICATION_STATUS) as NotificationStatus[]).map(status => ({
  value: status,
  label: STATUS_LABELS[status],
}))

const CATEGORY_OPTIONS = (Object.values(NOTIFICATION_CATEGORY) as NotificationCategory[]).map(category => ({
  value: category,
  label: CATEGORY_LABELS[category],
}))

/**
 * `getGridSingleSelectOperators()` leads with `is`, so multi-select is a hidden
 * second step. Promoting `isAnyOf` makes picking several values the default.
 */
const MULTI_SELECT_OPERATORS = (() => {
  const operators = getGridSingleSelectOperators()
  const isAnyOf = operators.filter(operator => operator.value === 'isAnyOf')
  return [...isAnyOf, ...operators.filter(operator => operator.value !== 'isAnyOf')]
})()

/**
 * `contains` only. The contract's `search` is a substring match across several columns, so `equals`,
 * `startsWith` and the empty-checks would all be promises the server can't keep — and an operator that
 * quietly does nothing is worse than one that isn't offered.
 */
const TEXT_SEARCH_OPERATORS = getGridStringOperators().filter(operator => operator.value === 'contains')

const READ_OPTIONS = [
  { value: READ_STATE.UNREAD, label: T.UNREAD },
  { value: READ_STATE.READ, label: T.READ },
]

/**
 * One visible column renders the whole card; the rest stay hidden but real so
 * search, filtering and export have fields to work with. `fixedField` is the
 * dimension the active view already pins, which drops out of the filter panel.
 */
export const buildNotificationColumns = (fixedField: string | undefined, areas: readonly string[]): GridColDef<Notification>[] => [
  { field: 'card', flex: 1, sortable: false, filterable: false, disableExport: true, renderCell: renderRowCard },
  { field: 'title', headerName: T.NOTIFICATION, type: 'string', filterable: true, filterOperators: TEXT_SEARCH_OPERATORS },
  // Real for search and export, but not offered in the panel: `search` already covers the body, and a
  // second text field mapping to the same param would just overwrite the first.
  { field: 'body', headerName: T.DETAILS, type: 'string', filterable: false },
  {
    field: 'area',
    headerName: T.BY_TYPE,
    type: 'singleSelect',
    valueOptions: areas.map(area => ({ value: area, label: area })),
    filterOperators: MULTI_SELECT_OPERATORS,
    filterable: fixedField !== NOTIFICATION_FILTER_FIELD.AREA,
    valueGetter: (value, row) => areaFor(row),
  },
  {
    field: 'category',
    headerName: T.BY_ACTIVITY,
    type: 'singleSelect',
    valueOptions: CATEGORY_OPTIONS,
    filterOperators: MULTI_SELECT_OPERATORS,
    filterable: fixedField !== NOTIFICATION_FILTER_FIELD.CATEGORY,
  },
  {
    field: 'read',
    headerName: T.INBOX,
    type: 'singleSelect',
    valueOptions: READ_OPTIONS,
    filterable: fixedField !== NOTIFICATION_FILTER_FIELD.READ,
    valueGetter: (value, row) => (isUnread(row) ? READ_STATE.UNREAD : READ_STATE.READ),
  },
  {
    field: 'status',
    headerName: T.STATUS,
    type: 'singleSelect',
    valueOptions: STATUS_OPTIONS,
    filterOperators: MULTI_SELECT_OPERATORS,
    filterable: true,
  },
  {
    field: 'occurredAt',
    headerName: T.RECEIVED,
    // `date` gives the panel a native date selector; its comparator reads
    // `value.getTime()`, so the column has to hand back a Date, not the ISO string.
    type: 'date',
    filterable: true,
    filterOperators: notificationDateOperators,
    valueGetter: (value, row) => new Date(row.occurredAt),
  },
]
